import type { CostPaymentRecordParam } from "../../types";
import { getDevPaymentRecordFixtures } from "./suggested/CostsOverviewConceptScreen";
import type { ApiPaymentRecord } from "@src/packages/costs/hooks/usePaymentRecords";
import type { TaxDeductionProfile } from "@src/packages/costs/hooks/useTaxDeductions";
import { toDecimalAmount } from "@src/packages/costs/decimalAmount";
import { parseDateText, uiToApiLineItems } from "./paymentRecordMapping";

export function getDevTaxReviewFixture(taxYear: number) {
  if (!__DEV__ || typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const emptyState = params.get("dev_tax_review_empty") === "1";
  if (!emptyState && params.get("dev_costs_payment_record") !== "filled") {
    return null;
  }

  const agi = Number(params.get("dev_tax_review_agi"));
  if (!Number.isFinite(agi) || agi < 0) return null;

  const itemization = params.get("dev_tax_review_itemization");
  const itemizesDeductions =
    itemization === "yes" ? true : itemization === "no" ? false : null;
  const showAllocationEvidence = params.get("dev_tax_review_offset") === "1";
  const omitProvider = params.get("dev_tax_review_missing_provider") === "1";
  const fixtureRecords = emptyState
    ? []
    : (getDevPaymentRecordFixtures() ?? []);
  const profile: TaxDeductionProfile = {
    id: "dev-tax-deduction-profile",
    patientId: "dev-patient",
    taxYear,
    adjustedGrossIncome: toDecimalAmount(agi),
    itemizesDeductions,
    filingStatus: null,
    stateCode: null,
    createdAt: `${taxYear}-01-01T00:00:00.000Z`,
    updatedAt: `${taxYear}-01-01T00:00:00.000Z`,
    updatedBy: null,
  };
  const records: ApiPaymentRecord[] = fixtureRecords
    .filter(
      (
        record,
      ): record is CostPaymentRecordParam & {
        kind: "payment" | "receipt";
      } => record.kind === "payment" || record.kind === "receipt",
    )
    .map((record, index) => {
      const parsedDate = parseDateText(record.date);
      const occurredOn = parsedDate
        ? `${taxYear}${parsedDate.slice(4)}`
        : `${taxYear}-06-${String(index + 8).padStart(2, "0")}`;

      const isPaymentWithAllocationEvidence =
        showAllocationEvidence && record.id === "dev-filled-payment";
      const lineItems = isPaymentWithAllocationEvidence
        ? uiToApiLineItems([
            {
              id: `dev-tax-${record.id}-line-item`,
              label: record.label,
              amount: record.amount,
              detail: record.detail,
              allocations: [
                {
                  id: `dev-tax-${record.id}-patient-allocation`,
                  kind: "patient" as const,
                  label: "You",
                  amount: 900,
                },
                {
                  id: `dev-tax-${record.id}-insurance-allocation`,
                  kind: "insurance" as const,
                  label: "Insurance",
                  amount: 300,
                },
              ],
            },
          ])
        : record.lineItems
          ? uiToApiLineItems(record.lineItems)
          : null;

      return {
        id: `dev-tax-${record.id}`,
        patientId: profile.patientId,
        kind: record.kind,
        entrySource: "manual",
        label: record.label,
        provider:
          omitProvider && record.kind === "receipt" ? null : record.provider,
        amount: toDecimalAmount(record.amount),
        billedAmount: toDecimalAmount(record.billedAmount),
        status: isPaymentWithAllocationEvidence
          ? "partially-covered"
          : record.status,
        occurredOn,
        note: record.detail,
        lineItems,
        documents: record.documents.map((document) => ({
          id: document.id,
          label: document.label,
          status: document.status,
          uploadId: document.uploadId ?? null,
        })),
        source: "patient",
        verificationStatus: "unverified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
        createdAt: `${taxYear}-01-01T00:00:00.000Z`,
        updatedAt: `${taxYear}-01-01T00:00:00.000Z`,
        updatedBy: null,
      };
    });

  return { records, reviews: [], profile };
}
